import { useMemo } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { useProfiles } from '@/features/users/hooks';
import { cn } from '@/lib/utils';

type OrgPersonId =
  | 'kevin'
  | 'kevin-sr'
  | 'jamie'
  | 'nick'
  | 'gaelan';

interface OrgPersonDef {
  id: OrgPersonId;
  name: string;
  /** Substrings used to match a live profile for avatar / display name. */
  matchNames: string[];
  title: string;
}

interface OrgGroupDef {
  id: string;
  label: string;
  memberIds: OrgPersonId[];
}

const CEO: OrgPersonDef = {
  id: 'kevin',
  name: 'Kevin Bartchlett',
  matchNames: ['kevin bartchlett', 'kevin bartlett'],
  title: 'CEO / Owner',
};

const REPORTS: OrgPersonDef[] = [
  {
    id: 'kevin-sr',
    name: 'Kevin Bartchlett (Sr.)',
    matchNames: ['kevin bartchlett (sr', 'kevin bartchlett sr', 'kevin sr'],
    title: 'Head of Product Realization',
  },
  {
    id: 'jamie',
    name: 'Jamie Taylor',
    matchNames: ['jamie taylor'],
    title: 'Head of Software, Waypoint',
  },
  {
    id: 'nick',
    name: 'Nick Prueter',
    matchNames: ['nick prueter'],
    title: 'COO',
  },
  {
    id: 'gaelan',
    name: 'Gaelan Brown',
    matchNames: ['gaelan brown'],
    title: 'CCO',
  },
];

/** Left-to-right groups under the CEO. Product spans Sr. + Jamie. */
const GROUPS: OrgGroupDef[] = [
  {
    id: 'product',
    label: 'Product',
    memberIds: ['kevin-sr', 'jamie'],
  },
  {
    id: 'operations',
    label: 'Operations',
    memberIds: ['nick'],
  },
  {
    id: 'sales-marketing',
    label: 'Sales & Marketing',
    memberIds: ['gaelan'],
  },
];

interface ResolvedPerson {
  id: OrgPersonId;
  name: string;
  title: string;
  avatarUrl: string | null;
}

function initials(name: string): string {
  const parts = name.replace(/\(.*?\)/g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

function matchProfile(
  def: OrgPersonDef,
  profiles: { full_name: string | null; avatar_url: string | null }[],
  used: Set<number>,
): { name: string; avatarUrl: string | null } {
  const lowered = def.matchNames.map((n) => n.toLowerCase());
  for (let i = 0; i < profiles.length; i++) {
    if (used.has(i)) continue;
    const full = (profiles[i]!.full_name ?? '').trim().toLowerCase();
    if (!full) continue;

    // Keep the two Kevins distinct when both exist in profiles.
    if (def.id === 'kevin-sr') {
      const isSr =
        /\(\s*sr\.?\s*\)/.test(full) ||
        /\bsr\.?\b/.test(full) ||
        full.includes('senior');
      if (!isSr) continue;
    }
    if (def.id === 'kevin') {
      const isSr =
        /\(\s*sr\.?\s*\)/.test(full) ||
        /\bsr\.?\b/.test(full) ||
        full.includes('senior');
      if (isSr) continue;
    }

    const hit = lowered.some(
      (needle) => full === needle || full.startsWith(needle) || full.includes(needle),
    );
    if (hit) {
      used.add(i);
      return {
        name: profiles[i]!.full_name?.trim() || def.name,
        avatarUrl: profiles[i]!.avatar_url,
      };
    }
  }
  return { name: def.name, avatarUrl: null };
}

function PersonCard({
  person,
  emphasis = false,
}: {
  person: ResolvedPerson;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex w-[12.5rem] flex-col items-center gap-2 rounded-lg border bg-card px-3 py-4 text-center shadow-[0_1px_2px_0_hsl(124_26%_14%/0.04)]',
        emphasis
          ? 'border-humus/25 ring-1 ring-humus/10'
          : 'border-border',
      )}
    >
      <Avatar className={cn(emphasis ? 'h-14 w-14' : 'h-11 w-11')}>
        {person.avatarUrl ? <AvatarImage src={person.avatarUrl} alt={person.name} /> : null}
        <AvatarFallback className={cn(emphasis ? 'text-sm' : 'text-xs')}>
          {initials(person.name)}
        </AvatarFallback>
      </Avatar>
      <div className="space-y-0.5">
        <p className="text-sm font-medium tracking-headline leading-headline text-foreground">
          {person.name}
        </p>
        <p className="text-xs leading-body text-muted-foreground">{person.title}</p>
      </div>
    </div>
  );
}

function GroupColumn({
  label,
  people,
}: {
  label: string;
  people: ResolvedPerson[];
}) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-3">
      <div className="rounded-full border border-info-soft/60 bg-info-soft/30 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-info">
        {label}
      </div>
      <div className="flex w-full flex-wrap items-start justify-center gap-3 rounded-xl border border-dashed border-border/80 bg-muted/30 px-3 pb-4 pt-5">
        {people.map((person) => (
          <PersonCard key={person.id} person={person} />
        ))}
      </div>
    </div>
  );
}

/**
 * HR → Org Chart. Static company reporting structure with optional avatar
 * enrichment from live profiles when names match.
 */
export function OrgChartPage() {
  const { data: profiles } = useProfiles();

  const { ceo, groups } = useMemo(() => {
    const list = (profiles ?? []).filter((p) => p.is_active && p.role !== 'external_lender');
    const used = new Set<number>();

    // Resolve reports first so Sr. can claim a distinct profile before CEO.
    const reportById = new Map<OrgPersonId, ResolvedPerson>();
    for (const def of REPORTS) {
      const matched = matchProfile(def, list, used);
      reportById.set(def.id, {
        id: def.id,
        name: def.id === 'kevin-sr' ? def.name : matched.name,
        title: def.title,
        avatarUrl: matched.avatarUrl,
      });
    }

    const ceoMatched = matchProfile(CEO, list, used);
    const ceoPerson: ResolvedPerson = {
      id: CEO.id,
      name: ceoMatched.name,
      title: CEO.title,
      avatarUrl: ceoMatched.avatarUrl,
    };

    const resolvedGroups = GROUPS.map((g) => ({
      id: g.id,
      label: g.label,
      people: g.memberIds.map((id) => reportById.get(id)!),
    }));

    return { ceo: ceoPerson, groups: resolvedGroups };
  }, [profiles]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="HR"
        title="Org Chart"
      />

      <div className="overflow-x-auto pb-2">
        <div className="mx-auto flex min-w-[56rem] flex-col items-center px-2 py-4">
          <PersonCard person={ceo} emphasis />

          {/* Trunk from CEO into the three reporting groups */}
          <div className="flex h-8 w-px bg-border" aria-hidden />
          <div className="relative w-full" aria-hidden>
            {/* Bar spans group centers: Product ~25%, Ops ~62.5%, Sales ~87.5% of a 2fr/1fr/1fr row */}
            <div className="absolute left-[25%] right-[12.5%] top-0 h-px bg-border" />
            <div className="grid grid-cols-[2fr_1fr_1fr] gap-6">
              <div className="flex justify-center">
                <div className="h-3 w-px bg-border" />
              </div>
              <div className="flex justify-center">
                <div className="h-3 w-px bg-border" />
              </div>
              <div className="flex justify-center">
                <div className="h-3 w-px bg-border" />
              </div>
            </div>
          </div>

          <div className="mt-3 grid w-full grid-cols-[2fr_1fr_1fr] items-start gap-6">
            {groups.map((group) => (
              <GroupColumn key={group.id} label={group.label} people={group.people} />
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
